import { createContext, ReactNode, useContext, useMemo } from 'react';
import type { Key } from '@react-types/shared';
import { GridList, GridListItem } from 'react-aria-components';
import { cn } from 'v5/platform/dom/cn';
import { CollectionDragHandle } from 'v5/patterns/dnd/CollectionDragHandle';
import {
  flexListDropIndicator,
  type DndListGap,
  type DndListRenderDropIndicator
} from 'v5/patterns/dnd/DndListDropIndicator';
import { useCollectionReorder } from 'v5/patterns/dnd/useCollectionReorder';

export type { DndListGap, DndListRenderDropIndicator };
export {
  flexListDropIndicator,
  gridListDropIndicator
} from 'v5/patterns/dnd/DndListDropIndicator';

export interface DndItemKit {
  /** React Aria drag handle (`slot="drag"`). `null` when sorting is disabled. */
  dragHandle: ReactNode;
}

const DndItemContext = createContext<DndItemKit | null>(null);

function useDndItemKit(): DndItemKit {
  const ctx = useContext(DndItemContext);
  if (!ctx) {
    throw new Error('DndList.Row must be used inside DndList');
  }
  return ctx;
}

const NOOP_KIT: DndItemKit = {
  dragHandle: null
};

/**
 * Default `GridListItem` shell — base surface, hover and the keyboard's focus. `hocus`, not React
 * Aria's `data-hovered`, which a row without an action or selection never gets.
 */
export const dndListItemShellClassName = cn(
  'group/dnd-item outline-none relative z-0 bg-bg-default transition-colors',
  'hocus:bg-bg-subtle'
);

/**
 * Nested surface inside `group/dnd-item` (e.g. bordered card row).
 * Pairs with {@link dndListItemShellClassName} on the parent `GridListItem`.
 */
export function dndListItemSurfaceClassName(className?: string) {
  return cn(
    'bg-bg-default transition-colors border border-transparent',
    'group-hocus/dnd-item:bg-bg-subtle group-hocus/dnd-item:border-border-default',
    className
  );
}

export type DndListRowProps = {
  children: ReactNode;
  className?: string;
};

/**
 * Flex row with a drag handle on the left. Must be used inside `DndList`.
 * Includes default hover/focus surface styles; add card chrome (border, padding, drag states) via `className`.
 */
function Row({ children, className }: DndListRowProps) {
  const kit = useDndItemKit();

  return (
    <div
      className={cn(
        'flex items-center gap-3 min-w-0 w-full',
        dndListItemSurfaceClassName(),
        className
      )}
    >
      {kit.dragHandle}
      <div className="flex-1 min-w-0">{children}</div>
    </div>
  );
}

export interface DndListProps<T extends object, TKey extends Key> {
  items: T[];
  getKey: (item: T) => TKey;
  /** Accessible name for the list and type-to-select labels. */
  getTextValue: (item: T) => string;
  /** Accessible label for the list. */
  ariaLabel: string;
  onReorder?: (orderedKeys: TKey[]) => void;
  /**
   * Render each item. Receives a `DndItemKit` with a RAC drag handle.
   *
   * **With `DndList.Row`**: flex row + handle on the left.
   *
   * **Custom layouts** (e.g. CSS grid subgrid): place `{kit.dragHandle}` in your row
   * and rely on `itemClassName` for the `GridListItem` shell.
   */
  children: (item: T, kit: DndItemKit) => ReactNode;
  emptyState?: ReactNode;
  className?: string;
  style?: React.CSSProperties;
  /** Pairs with `gap-*` on `className` for the default drop indicator. Default `3`. */
  gap?: DndListGap;
  /** Custom drop indicator; defaults to {@link flexListDropIndicator} for `gap`. */
  renderDropIndicator?: DndListRenderDropIndicator;
  /** Classes on each list item shell (sortable and non-sortable). */
  itemClassName?: string;
}

function DndListRoot<T extends object, TKey extends Key>({
  items,
  getKey,
  getTextValue,
  ariaLabel,
  onReorder,
  children,
  emptyState,
  className,
  style,
  gap = '3',
  renderDropIndicator: renderDropIndicatorProp,
  itemClassName
}: DndListProps<T, TKey>) {
  const sortableEnabled = Boolean(onReorder) && items.length > 1;

  const orderedKeys = useMemo(() => items.map(getKey), [items, getKey]);

  const textByKey = useMemo(() => {
    const map = new Map<string, string>();
    for (const item of items) {
      map.set(String(getKey(item)), getTextValue(item));
    }
    return map;
  }, [items, getKey, getTextValue]);

  const renderDropIndicator = useMemo(
    () => renderDropIndicatorProp ?? flexListDropIndicator(gap, orderedKeys[0]),
    [gap, orderedKeys, renderDropIndicatorProp]
  );

  const { dragAndDropHooks } = useCollectionReorder({
    orderedKeys,
    onReorder,
    textByKey,
    renderDropIndicator,
    enabled: sortableEnabled
  });

  const itemShellClassName = cn(dndListItemShellClassName, itemClassName);

  if (items.length === 0) {
    return (
      <div
        className={cn('flex items-center justify-center py-8', className)}
        style={style}
        role="list"
        aria-label={ariaLabel}
      >
        {typeof emptyState === 'string' ? (
          <span className="text-center text-fg-muted">{emptyState}</span>
        ) : (
          emptyState
        )}
      </div>
    );
  }

  if (!sortableEnabled) {
    return (
      <div
        className={className}
        style={style}
        role="list"
        aria-label={ariaLabel}
      >
        {items.map(item => (
          <div
            key={String(getKey(item))}
            role="listitem"
            className={itemShellClassName}
          >
            <DndItemContext.Provider value={NOOP_KIT}>
              {children(item, NOOP_KIT)}
            </DndItemContext.Provider>
          </div>
        ))}
      </div>
    );
  }

  return (
    <GridList
      items={items}
      aria-label={ariaLabel}
      selectionMode="none"
      dragAndDropHooks={dragAndDropHooks}
      className={cn('outline-none', className)}
      style={style}
    >
      {item => {
        const kit: DndItemKit = {
          dragHandle: (
            <CollectionDragHandle label={`Reorder ${getTextValue(item)}`} />
          )
        };

        return (
          <GridListItem
            id={getKey(item)}
            textValue={getTextValue(item)}
            className={itemShellClassName}
          >
            <DndItemContext.Provider value={kit}>
              {children(item, kit)}
            </DndItemContext.Provider>
          </GridListItem>
        );
      }}
    </GridList>
  );
}

export const DndList = Object.assign(DndListRoot, { Row });
